import clsx from "clsx";
import React from "react";
import Highlighter from "react-highlight-words";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { Overlay } from "src/types/osbuilder";
import { makeStyles } from "tss-react/mui";

import {
    AndroidModule,
    ApplicationModule,
    DownloadableFileModule,
    ExecutableModule,
    PartitionLayoutModule,
    PrebuiltBootAnimationModule,
    RemoveModule,
    Sources,
} from "@dashboard/products/types";
import { Box, Card, CardActions, CardContent, Theme, Typography } from "@mui/material";
import { DangerButton, PrimaryButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { Tooltip } from "@shared/Tooltip";

import { getTagReactKey, sortTags } from "../../../../tags";
import { Tag } from "../../../Tag/Tag";
import Utils from "../ModulePanel.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    card: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
    },
    childrenBox: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(1),
        columnGap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    detailRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
        width: "100%",
    },
    detailRowFullWidth: {
        gridColumn: "1 / -1",
    },
    detailLabel: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        flexShrink: 0,
    },
    detailValue: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
    },
    cardActions: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    title: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "inline-block",
        maxWidth: "100%",
        minWidth: 0,
    },
    fullWidth: {
        width: "100%",
    },
    tagsContainer: {
        display: "flex",
        flexDirection: "row",
        marginBottom: theme.spacing(1),
        gap: theme.spacing(1),
    },
    addedModule: {
        backgroundColor: theme.palette.emteria.background.paper,
    },
}));

type BaseCardProps = {
    title: string;
    description: string;
    tags: string[];
    onAdd?: () => void;
    onEdit?: () => void;
    onRemove?: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};
const BaseCard = (props: React.PropsWithChildren<BaseCardProps>) => {
    const [hasRequestedChange, setHasRequestedChange] = React.useState(false);
    const { search } = Utils.useSearch();
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);

    const isAddedModule = props.tags.some((tag) => tag === "Added");
    const isDisabled = Boolean(props.isDisabled);
    const isUpdating = hasRequestedChange && isDisabled;

    const onAdd = () => {
        setHasRequestedChange(true);
        props.onAdd?.();
    };
    const onRemove = () => {
        setHasRequestedChange(true);
        props.onRemove?.();
    };

    if (hasRequestedChange && !isDisabled) {
        setHasRequestedChange(false);
    }

    const titleMatch = props.title.toLowerCase().includes(search.toLowerCase());
    const descriptionMatch = props.description.toLowerCase().includes(search.toLowerCase());
    if (!titleMatch && !descriptionMatch) {
        return null;
    }

    return (
        <Card className={clsx(classes.card, isAddedModule && classes.addedModule)}>
            <CardContent>
                <Box className={clsx(classes.fullWidth, classes.tagsContainer)}>
                    {sortTags<API.Tag>(props.tags.map((tag) => ({ key: tag, value: "", type: "simple" }))).map((item) => (
                        <Tag key={getTagReactKey(item)} theme="light" item={item} />
                    ))}
                </Box>
                <Box>
                    <Typography variant="h6" className={classes.title}>
                        <Highlighter searchWords={[search]} textToHighlight={props.title} autoEscape />
                    </Typography>
                </Box>
                <Typography>
                    <Highlighter searchWords={[search]} textToHighlight={props.description} autoEscape />
                </Typography>
                <Box className={classes.childrenBox}>{props.children}</Box>
            </CardContent>
            <CardActions className={classes.cardActions}>
                {props.onAdd ? (
                    <Tooltip hide={!props.tooltip} title={props.tooltip}>
                        <PrimaryButton onClick={onAdd} disabled={isDisabled} loading={isUpdating}>
                            {t("common:button.add")}
                        </PrimaryButton>
                    </Tooltip>
                ) : null}
                {props.onEdit ? (
                    <Tooltip hide={!props.tooltip} title={props.tooltip}>
                        <PrimaryOutlinedButton onClick={props.onEdit} disabled={isDisabled}>
                            {t("common:button.edit")}
                        </PrimaryOutlinedButton>
                    </Tooltip>
                ) : null}
                {props.onRemove ? (
                    <Tooltip hide={!props.tooltip} title={props.tooltip}>
                        <DangerButton onClick={onRemove} disabled={isDisabled} loading={isUpdating}>
                            {t("common:button.remove")}
                        </DangerButton>
                    </Tooltip>
                ) : null}
            </CardActions>
        </Card>
    );
};

const getEmteriaPackageTranslations = (name: string | undefined) => {
    switch (name) {
        case "com.emteria.headless":
            return {
                name: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaHeadless.name"),
                description: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaHeadless.description"),
            };
        case "com.emteria.kiosk":
            return {
                name: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaKiosk.name"),
                description: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaKiosk.description"),
            };
        case "com.emteria.mdm":
            return {
                name: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaMdm.name"),
                description: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaMdm.description"),
            };
        case "com.emteria.storage":
            return {
                name: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaStorage.name"),
                description: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaStorage.description"),
            };
        case "com.emteria.update":
            return {
                name: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaUpdate.name"),
                description: i18n.t("sidepanels:modulePanel.ctaModules.addEmteriaUpdate.description"),
            };
        default:
            return null;
    }
};

type ExistingModuleCardProps = {
    module: AndroidModule;
    onRemove: () => void;
    onDirectAddModule: () => void;
    onIncludeSystemPackage: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};
export const ExistingModuleCard = (props: React.PropsWithChildren<ExistingModuleCardProps>) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);

    const isSystemModule = props.module.tags.some((tag) => tag === "System");
    const isModuleAdded = props.module.tags.some((tag) => tag === "Added");

    const moduleCardProps: BaseCardProps = {
        title: "-",
        description: "-",
        tags: props.module.tags,
    };

    const translations = getEmteriaPackageTranslations(props.module.name);
    if (translations) {
        moduleCardProps.title = translations.name;
        moduleCardProps.description = translations.description;

        if (isModuleAdded) {
            moduleCardProps.onRemove = () => props.onRemove();
        } else {
            moduleCardProps.onAdd = () => props.onDirectAddModule();
        }
    } else {
        moduleCardProps.title = i18n.t("sidepanels:modulePanel.ctaModules.includeSystemPackage.name");
        moduleCardProps.description = i18n.t("sidepanels:modulePanel.ctaModules.includeSystemPackage.description");

        if (isModuleAdded) {
            moduleCardProps.onRemove = () => props.onRemove();
        } else {
            moduleCardProps.onAdd = () => props.onIncludeSystemPackage();
        }
    }
    if (isModuleAdded || (!isSystemModule && !isModuleAdded)) {
        moduleCardProps.isDisabled = props.isDisabled;
        moduleCardProps.tooltip = props.tooltip;
    }

    if (!isModuleAdded) {
        return <BaseCard {...moduleCardProps} />;
    }

    return (
        <BaseCard {...moduleCardProps}>
            <Box className={classes.detailRow}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.name")}:
                </Typography>
                <Tooltip title={props.module.name} className={classes.detailValue}>
                    <Typography fontWeight="bold" variant="caption">
                        {props.module.name}
                    </Typography>
                </Tooltip>
            </Box>
        </BaseCard>
    );
};

type PrebuiltApplicationCardProps = {
    module: ApplicationModule;
    onAdd: () => void;
    onEdit: () => void;
    onRemove: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};
export const PrebuiltApplicationCard = (props: React.PropsWithChildren<PrebuiltApplicationCardProps>) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels", "modals"]);

    const isModuleAdded = props.module.tags.some((tag) => tag === "Added");

    const title = t("sidepanels:modulePanel.ctaModules.addOwnApplication.name");
    const description = t("sidepanels:modulePanel.ctaModules.addOwnApplication.description");

    if (!isModuleAdded) {
        return <BaseCard title={title} description={description} tags={props.module.tags} onAdd={props.onAdd} />;
    }

    return (
        <BaseCard
            title={title}
            description={description}
            tags={props.module.tags}
            onEdit={props.onEdit}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
        >
            <Box className={classes.detailRow}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.name")}:
                </Typography>
                <Tooltip title={props.module.name} className={classes.detailValue}>
                    <Typography fontWeight="bold" variant="caption">
                        {props.module.name}
                    </Typography>
                </Tooltip>
            </Box>

            <Box className={classes.detailRow}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.partition")}:
                </Typography>
                <Typography fontWeight="bold" variant="caption">
                    {props.module.partition}
                </Typography>
            </Box>

            <Box className={classes.detailRow}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.privileged")}:
                </Typography>
                <Typography fontWeight="bold" variant="caption">
                    {props.module.privileged
                        ? t("modals:manageProductModules.application.input.privileged.options.privileged")
                        : t("modals:manageProductModules.application.input.privileged.options.regular")}
                </Typography>
            </Box>

            <Box className={classes.detailRow}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.certificate")}:
                </Typography>
                <Typography fontWeight="bold" variant="caption">
                    {props.module.certificate}
                </Typography>
            </Box>
        </BaseCard>
    );
};

type PrebuiltExecutableCardProps = {
    module: ExecutableModule;
    onAdd: () => void;
    onEdit: () => void;
    onRemove: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};
export const PrebuiltExecutableCard = (props: React.PropsWithChildren<PrebuiltExecutableCardProps>) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels"]);

    const isModuleAdded = props.module.tags.some((tag) => tag === "Added");

    const title = t("sidepanels:modulePanel.ctaModules.addOwnExecutable.name");
    const description = t("sidepanels:modulePanel.ctaModules.addOwnExecutable.description");

    if (!isModuleAdded) {
        return <BaseCard title={title} description={description} tags={props.module.tags} onAdd={props.onAdd} />;
    }

    return (
        <BaseCard
            title={title}
            description={description}
            tags={props.module.tags}
            onEdit={props.onEdit}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
        >
            <Box className={classes.detailRow}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.name")}:
                </Typography>
                <Tooltip title={props.module.name} className={classes.detailValue}>
                    <Typography fontWeight="bold" variant="caption">
                        {props.module.name}
                    </Typography>
                </Tooltip>
            </Box>
        </BaseCard>
    );
};

type EditableFileModuleCardProps = {
    module: PrebuiltBootAnimationModule | PartitionLayoutModule;
    sources: Sources;
    onAdd: () => void;
    onEdit: () => void;
    onRemove: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};

const EditableFileModuleCard = (props: EditableFileModuleCardProps & { title: string; description: string }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels"]);
    const { module, sources } = props;

    const source = module.sourceId ? sources[module.sourceId] : undefined;
    const hasValidSource = Boolean(source);
    const displayFilename = hasValidSource ? (source!.filename ?? "—") : t("sidepanels:modulePanel.sourceFileMissing");

    const isModuleAdded = module.tags.some((tag) => tag === "Added");

    if (!isModuleAdded) {
        return <BaseCard title={props.title} description={props.description} tags={module.tags} onAdd={props.onAdd} />;
    }

    return (
        <BaseCard
            title={props.title}
            description={props.description}
            tags={module.tags}
            onEdit={props.onEdit}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
        >
            <Box className={clsx(classes.detailRow, classes.detailRowFullWidth)}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.file")}:
                </Typography>
                <Tooltip title={displayFilename} className={classes.detailValue}>
                    <Typography fontWeight="bold" variant="caption" color={hasValidSource ? undefined : "error"}>
                        {displayFilename}
                    </Typography>
                </Tooltip>
            </Box>
        </BaseCard>
    );
};

export const PrebuiltBootAnimationCard = (props: EditableFileModuleCardProps & { module: PrebuiltBootAnimationModule }) => {
    const { t } = useTranslation(["sidepanels"]);

    return (
        <EditableFileModuleCard
            {...props}
            title={t("sidepanels:modulePanel.ctaModules.addOwnBootAnimation.name")}
            description={t("sidepanels:modulePanel.ctaModules.addOwnBootAnimation.description")}
        />
    );
};

export const PartitionLayoutCard = (props: EditableFileModuleCardProps & { module: PartitionLayoutModule }) => {
    const { t } = useTranslation(["sidepanels"]);

    return (
        <EditableFileModuleCard
            {...props}
            title={t("sidepanels:modulePanel.ctaModules.addPartitionLayout.name")}
            description={t("sidepanels:modulePanel.ctaModules.addPartitionLayout.description")}
        />
    );
};

type RemoveOverrideCardProps = {
    module: RemoveModule;
    onAdd: () => void;
    onRemove: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};
export const RemoveOverrideCard = (props: React.PropsWithChildren<RemoveOverrideCardProps>) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels"]);

    const isModuleAdded = props.module.tags.some((tag) => tag === "Added");

    const title = t("sidepanels:modulePanel.ctaModules.excludeSystemPackage.name");
    const description = t("sidepanels:modulePanel.ctaModules.excludeSystemPackage.description");

    if (!isModuleAdded) {
        return <BaseCard title={title} description={description} tags={props.module.tags} onAdd={props.onAdd} />;
    }

    return (
        <BaseCard
            title={title}
            description={description}
            tags={props.module.tags}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
        >
            <Box className={classes.detailRow}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.name")}:
                </Typography>
                <Tooltip title={props.module.name} className={classes.detailValue}>
                    <Typography fontWeight="bold" variant="caption">
                        {props.module.name}
                    </Typography>
                </Tooltip>
            </Box>
        </BaseCard>
    );
};

type DownloadableFileCardProps = {
    module: DownloadableFileModule;
    sources: Sources;
    onAdd: () => void;
    onEdit: () => void;
    onRemove: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};

export const DownloadableFileCard = (props: DownloadableFileCardProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels"]);
    const { module, sources } = props;

    const source = module.sourceId ? sources[module.sourceId] : undefined;
    const hasValidSource = Boolean(source);
    const displayFilename = hasValidSource ? (source!.filename ?? "—") : t("sidepanels:modulePanel.sourceFileMissing");

    const isModuleAdded = module.tags.some((tag) => tag === "Added");

    const title = t("sidepanels:modulePanel.ctaModules.addOwnDownloadableFile.name");
    const description = t("sidepanels:modulePanel.ctaModules.addOwnDownloadableFile.description");

    if (!isModuleAdded) {
        return <BaseCard title={title} description={description} tags={module.tags} onAdd={props.onAdd} />;
    }

    return (
        <BaseCard
            title={title}
            description={description}
            tags={module.tags}
            onEdit={props.onEdit}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
        >
            <Box className={clsx(classes.detailRow, classes.detailRowFullWidth)}>
                <Typography variant="caption" className={classes.detailLabel}>
                    {t("common:label.file")}:
                </Typography>
                <Tooltip title={displayFilename} className={classes.detailValue}>
                    <Typography fontWeight="bold" variant="caption" color={hasValidSource ? undefined : "error"}>
                        {displayFilename}
                    </Typography>
                </Tooltip>
            </Box>
        </BaseCard>
    );
};

type DetailRowProps = {
    label: string;
    value: string;
    fullWidth?: boolean;
};
const DetailRow = (props: DetailRowProps) => {
    const { classes } = useStyles();

    return (
        <Box className={clsx(classes.detailRow, props.fullWidth && classes.detailRowFullWidth)}>
            <Typography variant="caption" className={classes.detailLabel}>
                {props.label}:
            </Typography>
            <Tooltip title={props.value} className={classes.detailValue}>
                <Typography fontWeight="bold" variant="caption">
                    {props.value}
                </Typography>
            </Tooltip>
        </Box>
    );
};

type CustomOverlayCardProps = {
    overlay?: Overlay;
    onAdd?: () => void;
    onRemove?: () => void;
    isDisabled?: boolean;
    tooltip?: string;
};

export const CustomOverlayCard = (props: CustomOverlayCardProps) => {
    const { t } = useTranslation(["common", "modals", "sidepanels"]);
    const { overlay } = props;

    const title = t("sidepanels:modulePanel.ctaModules.addCustomOverlay.name");
    const description = t("sidepanels:modulePanel.ctaModules.addCustomOverlay.description");

    if (!overlay) {
        return (
            <BaseCard
                title={title}
                description={description}
                tags={["Build"]}
                onAdd={props.onAdd}
                isDisabled={props.isDisabled}
                tooltip={props.tooltip}
            />
        );
    }

    return (
        <BaseCard
            title={title}
            description={description}
            tags={["Added", "Build"]}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
        >
            <DetailRow
                label={t("modals:manageProductModules.addOverlay.input.resourceName.caption")}
                value={overlay.resourceName}
            />
            <DetailRow
                label={t("modals:manageProductModules.addOverlay.input.resourceType.caption")}
                value={overlay.resourceType}
            />
            <DetailRow
                label={t("modals:manageProductModules.addOverlay.input.targetPackage.caption")}
                value={overlay.targetPackage}
                fullWidth
            />
            <DetailRow
                label={t("modals:manageProductModules.addOverlay.input.resourcePath.caption")}
                value={overlay.resourcePath}
                fullWidth
            />
            <DetailRow label={t("common:label.value")} value={overlay.value} />
            {overlay.targetName ? (
                <DetailRow
                    label={t("modals:manageProductModules.addOverlay.input.targetName.caption")}
                    value={overlay.targetName}
                />
            ) : null}
        </BaseCard>
    );
};
